import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, FileBarChart2, ListChecks, Loader2, RefreshCw } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { FillRunner as FillModal } from '@/components/polls/FillRunner2';
import { PollClientPickDialog } from '@/components/polls/PollClientPickDialog';
import { PollManualProtocolDialog } from '@/components/polls/PollManualProtocolDialog';
import { PollTestActions } from '@/components/polls/PollTestActions';
import { PollMethodsCatalog } from '@/components/polls/PollMethodsCatalog';
import { PollProtocolCards } from '@/components/polls/PollProtocolCards';
import { MethodCategoryChip } from '@/components/polls/PollMethodCard';
import { loadPollMethodCatalog, methodMeta, type PollMethodItem } from '@/components/polls/pollMethods';
import { PsyReportsPanel } from '@/components/psy/PsyReportsPanel';
import {
    LEADING_COLS, TECH_COLS, COL_LABELS, PSY_SESSION_FIELD, hasPsyClientsCatalog, resolvePollType,
    findMethodologyReportId, type PollTypeInfo, type PollQuestionInfo, type ProtocolColumn, type ProtocolRow,
} from '@/lib/polls';

// ============================================================================
// FilledPollsPage — ТИП опроса (тест/анкета): ПРОТОКОЛЫ прохождения (физтаблица
// {code}) + ВОПРОСЫ методики (шаблон) + «Пройти» (раннер FillRunner). Клик по типу
// в дереве конфигуратора открывает страницу (?code=), а в ПРЕДПРИЯТИИ тот же UI
// рендерится через проп `code` (ObjectList/SectionPage ветка objectType='poll') —
// ЕДИНЫЙ компонент для обоих режимов (без дублирования рендера теста).
//
// СПИСОК МЕТОДИК — единый каталог с поиском и категориями (PollMethodsCatalog):
// категория выводится из реальных признаков типа (scoring + число вопросов), а не
// из несуществующего поля API.
//
// ИНВАРИАНТ (причина прежней «пустой формы»): колонки протокола берутся ИЗ
// МЕТАДАННЫХ типа (parent_type='poll'; сервер отдаёт их в ListTableData.columns),
// а НЕ выводятся из строк данных. Строки приходят proto-формата
// ({id, values:[{column,value}]}) и разворачиваются в плоские объекты.
//
// МОБИЛЬНАЯ ВЁРСТКА: протоколы — карточками (PollProtocolCards), таблиц нет;
// шапка и действия переносятся, широких фиксированных ширин нет.
// ============================================================================

export function FilledPollsPage({ code: codeProp, onSelectType }: { code?: string; onSelectType?: (code: string) => void } = {}) {
    const showError = useToastStore((s) => s.showError);
    const [searchParams] = useSearchParams();
    // Идентификатор методики: проп (Предприятие/ВИДЖЕТ — state-based) ИЛИ ?code=
    // (конфигуратор /polls-fill). Пусто → ЕДИНЫЙ компонент показывает СПИСОК
    // МЕТОДИК. Внешняя навигация несёт КОД (objectName объекта раздела), но для
    // устойчивости тип резолвится и по синониму (resolvePollType).
    const [pickedCode, setPickedCode] = useState('');
    const incoming = codeProp || searchParams.get('code') || '';
    const identifier = incoming || pickedCode;
    const [rows, setRows] = useState<ProtocolRow[]>([]);
    const [columns, setColumns] = useState<ProtocolColumn[]>([]);
    const [pollName, setPollName] = useState('');
    const [questions, setQuestions] = useState<PollQuestionInfo[]>([]);
    // Вопросы методики — ЯВНОЕ состояние (загрузка/готово/ошибка): «вечной
    // загрузки» и проглоченных ошибок нет (ошибка/пусто показываются текстом).
    const [qState, setQState] = useState<'loading' | 'ready' | 'error'>('loading');
    const [qError, setQError] = useState('');
    const [showQuestions, setShowQuestions] = useState(true);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [filling, setFilling] = useState<{ id: string; name: string } | null>(null);
    const [fillValues, setFillValues] = useState<Record<string, any>>({});
    // psy-методика (session_id у типа + справочник psy_clients в базе): перед
    // прохождением ОБЯЗАТЕЛЬНО выбирается клиент → subject_id протокола.
    const [psyMode, setPsyMode] = useState(false);
    const [clientPick, setClientPick] = useState(false);
    const [fillSubject, setFillSubject] = useState<{ id: string; name: string } | null>(null);
    // Ручной протокол внешней методики (диалог) + отчёт методики: кнопка «Отчёт»
    // показывается только если отчёт найден (связь «методика → отчёт» по имени).
    const [manualOpen, setManualOpen] = useState(false);
    const [reportId, setReportId] = useState('');
    // Каталог методик — единый источник списка, типов и производных признаков.
    const [catalog, setCatalog] = useState<PollMethodItem[]>([]);
    const [catalogWarnings, setCatalogWarnings] = useState<string[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [catalogLoaded, setCatalogLoaded] = useState(false);
    const [catalogError, setCatalogError] = useState<string | null>(null);
    // Отчёты по методикам и печать «Заключение по тесту» — отдельный экран
    // (мобильная вёрстка без таблиц): список отчётов, отборы, просмотр, печать.
    const [showReports, setShowReports] = useState(false);

    // Каталог методик (SSoT — сервер): типы опросов + число вопросов.
    const loadCatalog = useCallback(async (force = false) => {
        setCatalogLoading(true);
        try {
            const result = await loadPollMethodCatalog({ force });
            setCatalog(result.items);
            setCatalogWarnings(result.warnings);
            setCatalogError(null);
        } catch (e: any) {
            const msg = friendlyError(e);
            setCatalogError(msg);
            showError(msg);
        } finally {
            setCatalogLoading(false);
            setCatalogLoaded(true);
        }
    }, [showError]);

    useEffect(() => { void loadCatalog(); }, [loadCatalog]);

    // Типы опросов — производные от каталога (отдельных запросов нет).
    const types = useMemo<PollTypeInfo[]>(
        () => catalog.map((m) => ({
            code: m.code, name: m.name, synonym: m.synonym,
            status: m.status, scoring: m.scoring, templateId: m.templateId,
        })),
        [catalog],
    );

    // Резолв типа по идентификатору (код → синоним → имя): внешняя навигация
    // может нести КОД или СИНОНИМ методики; ниже везде используется type.code.
    const resolvedType = useMemo(() => resolvePollType(types, identifier), [types, identifier]);
    const typesReady = catalogLoaded || catalogError !== null;
    const code = resolvedType?.code || identifier;
    const catalogItem = useMemo(
        () => (resolvedType ? catalog.find((m) => m.code === resolvedType.code) : undefined),
        [catalog, resolvedType],
    );

    const load = useCallback(async () => {
        if (!code || !typesReady) return;
        setLoading(true);
        setLoadError('');
        try {
            const sdk = getSDK();
            // 1) ПРОТОКОЛЫ. Колонки — из МЕТАДАННЫХ типа (сервер строит их по
            //    parent_type='poll'), НЕ из строк: иначе без протоколов таблица
            //    была бы без колонок (пустая страница).
            const r: any = await sdk.metadata.listTableData({ schema: 'public', table: code, pageSize: 500, page: 0 });
            const rawNames: string[] = ((r?.columns || []) as any[]).map((c) => String(c?.name || ''));
            const cols: ProtocolColumn[] = ((r?.columns || []) as any[])
                .map((c) => ({ name: String(c.name || ''), label: COL_LABELS[String(c.name)] || String(c.name || ''), dataType: String(c.dataType || '') }))
                .filter((c) => c.name && !TECH_COLS.has(c.name));
            cols.sort((a, b) => {
                const ia = LEADING_COLS.indexOf(a.name), ib = LEADING_COLS.indexOf(b.name);
                if (ia >= 0 && ib >= 0) return ia - ib;
                if (ia >= 0) return -1;
                if (ib >= 0) return 1;
                return a.name.localeCompare(b.name);
            });
            setColumns(cols);
            // Строки proto-формата { id, values:[{column,value}] } → плоские объекты.
            const items: ProtocolRow[] = ((r?.rows || []) as any[]).map((row) => {
                const flat: ProtocolRow = { id: row.id };
                for (const v of (row.values || [])) flat[String(v.column)] = v.value;
                return flat;
            });
            setRows(items);

            // 2) Имя типа + признак psy-методики: реквизит «Сессия» (session_id)
            //    есть у типов поставки psy И в базе существует справочник
            //    psy_clients → перед прохождением выбирается КЛИЕНТ. При сбое
            //    проверки справочника ведём себя как psy (fail-closed: клиент
            //    обязателен) — ошибка уходит в журнал.
            setPollName(resolvedType?.synonym || resolvedType?.name || code);
            if (rawNames.includes(PSY_SESSION_FIELD)) {
                try {
                    setPsyMode(await hasPsyClientsCatalog());
                } catch (e) {
                    logErrorToJournal(e, 'Проверка справочника psy_clients');
                    setPsyMode(true);
                }
            } else {
                setPsyMode(false);
            }
        } catch (e: any) {
            const msg = friendlyError(e);
            setLoadError(msg);
            showError(msg);
        } finally {
            setLoading(false);
        }
    }, [code, resolvedType, typesReady, showError]);

    useEffect(() => { void load(); }, [load]);

    // ВОПРОСЫ методики — отдельный шаг со СВОИМ состоянием: ошибка вопросов не
    // ломает список протоколов, а пользователь видит явный текст.
    const loadQuestions = useCallback(async () => {
        // Каталог ещё не загружен — это не ошибка методики: ждём резолв типа.
        if (!typesReady && identifier) { setQState('loading'); setQError(''); return; }
        const templateId = resolvedType?.templateId;
        if (!templateId) {
            setQuestions([]);
            setQError('к типу методики не привязан шаблон вопросов');
            setQState('error');
            return;
        }
        setQState('loading');
        setQError('');
        try {
            const full: any = await getSDK().poll.getPollFull({ id: templateId });
            setQuestions((full?.questions || []) as PollQuestionInfo[]);
            setQState('ready');
        } catch (e: any) {
            logErrorToJournal(e, `Загрузка вопросов методики «${resolvedType?.synonym || code}»`);
            setQError(friendlyError(e));
            setQuestions([]);
            setQState('error');
        }
    }, [resolvedType?.templateId, resolvedType?.synonym, code, typesReady, identifier]);

    useEffect(() => { void loadQuestions(); }, [loadQuestions]);

    // Отчёт методики (поставка psy: имя отчёта = синониму методики): найден —
    // кнопка «Отчёт»; нет отчёта/ошибка — кнопки нет (ошибка — в журнал).
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try { const rid = await findMethodologyReportId(resolvedType); if (!cancelled) setReportId(rid); }
            catch (e) { logErrorToJournal(e, 'Поиск отчёта методики'); if (!cancelled) setReportId(''); }
        })();
        return () => { cancelled = true; };
    }, [resolvedType]);

    // Выбор методики: внешний обработчик (виджет/страница) ИЛИ внутренний переход
    // к тесту. Всегда передаём КОД типа (t.code) — синоним только ОТОБРАЖАЕТСЯ.
    const openType = useCallback((typeCode: string) => {
        if (onSelectType) onSelectType(typeCode);
        else setPickedCode(typeCode);
    }, [onSelectType]);

    const openFill = (subject: { id: string; name: string } | null) => {
        if (!resolvedType?.templateId) return;
        setFillValues({});
        setFillSubject(subject);
        setFilling({ id: resolvedType.templateId, name: resolvedType.synonym || resolvedType.name || code });
    };

    const startFill = () => {
        if (!resolvedType?.templateId) { showError('Не найден шаблон вопросов для этого типа'); return; }
        if (qState !== 'ready') { showError('Вопросы методики ещё не загружены — дождитесь загрузки или нажмите «Повторить»'); return; }
        if (questions.length === 0) {
            showError('В этой методике нет вопросов: внешняя методика — результат вносится вручную, кнопка «Создать протокол»');
            return;
        }
        // psy-методика: СНАЧАЛА выбор клиента (объект тестирования), без него
        // прохождение не запускается (запись уйдёт в subject_id протокола).
        if (psyMode) { setClientPick(true); return; }
        openFill(null);
    };

    const submitFill = async () => {
        if (!filling) return;
        if (psyMode && !fillSubject?.id) { showError('Не выбран клиент — прохождение не запущено'); return; }
        try {
            const answers = Object.entries(fillValues).map(([qid, v]) => ({ questionId: qid, valueJson: JSON.stringify(v ?? '') }));
            await getSDK().poll.submitPollAnswers({ pollId: filling.id, subjectId: fillSubject?.id || '', attempt: 0, answers });
            useToastStore.getState().showSuccess('Анкета отправлена');
            setFilling(null);
            setFillSubject(null);
            await load();
        } catch (e: any) { showError(friendlyError(e)); }
    };

    // Отчёты и печать — отдельный экран ЭТОГО компонента (та же оболочка, без
    // перехода в конфигуратор): открывается из списка методик и из карточки теста.
    if (showReports) {
        return (
            <div className="h-full min-h-0">
                <PsyReportsPanel
                    initialMethodCode={identifier ? code : ''}
                    onBack={() => setShowReports(false)}
                />
            </div>
        );
    }

    // РЕЖИМ СПИСКА МЕТОДИК: каталог с поиском и категориями. Клик открывает тест
    // (прохождение + протоколы) в ЭТОМ ЖЕ компоненте по КОДУ — «Создать»/пустая
    // автоформа невозможны: у типа опроса нет пользовательских реквизитов.
    if (!identifier) {
        return (
            <div className="flex h-full min-h-0 flex-col">
                <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
                    <ListChecks className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">Тесты и анкеты</span>
                    {catalogLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] ml-auto" onClick={() => setShowReports(true)} title="Отчёты по методикам и печать «Заключение по тесту»">
                        <FileBarChart2 className="h-3 w-3" /> Отчёты
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => void loadCatalog()} disabled={catalogLoading}>
                        <RefreshCw className={`h-3 w-3 ${catalogLoading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-1">
                    <PollMethodsCatalog
                        items={catalog}
                        loading={catalogLoading}
                        error={catalogError || undefined}
                        warnings={catalogWarnings}
                        onRetry={() => void loadCatalog(true)}
                        onOpen={(m) => openType(m.code)}
                        emptyTitle="Методик нет"
                        emptyText="Добавьте типы опросов в конфигураторе или установите поставку с методиками."
                        searchPlaceholder="Поиск методики: название, код, описание…"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
                {!incoming && (
                    <Button size="sm" variant="ghost" className="h-7 px-1.5 text-[11px]" onClick={() => setPickedCode('')}>
                        <ChevronLeft className="h-3.5 w-3.5" /> К списку
                    </Button>
                )}
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {catalogItem?.synonym || pollName || 'Протоколы прохождения'}
                </span>
                {catalogItem && <MethodCategoryChip category={catalogItem.category} />}
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => void load()} disabled={loading}>
                    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                </Button>
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setShowReports(true)} title="Отчёты по методикам и печать «Заключение по тесту»">
                    <FileBarChart2 className="h-3 w-3" /> Отчёты
                </Button>
                <PollTestActions
                    qState={qState}
                    questionsCount={questions.length}
                    psyMode={psyMode}
                    reportId={reportId}
                    onFill={() => void startFill()}
                    onManual={() => setManualOpen(true)}
                />
            </div>

            {catalogItem && (catalogItem.description || catalogItem.questionCount !== null) && (
                <div className="shrink-0 border-b px-3 py-1.5 text-[11px] text-muted-foreground">
                    {catalogItem.description}
                    {catalogItem.description ? ' · ' : ''}{methodMeta(catalogItem)}
                </div>
            )}

            {/* Вопросы методики — ЯВНЫЕ состояния: загрузка / ошибка (с «Повторить») /
                пусто (внешняя методика) / готово (раскрывающийся список). */}
            {qState !== 'ready' && (
                <div className="shrink-0 border-b">
                    {qState === 'loading' && (
                        <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />Загрузка вопросов методики…
                        </div>
                    )}
                    {qState === 'error' && (
                        <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
                            <span className="text-destructive">Вопросы методики не загружены: {qError}</span>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => void loadQuestions()}>Повторить</Button>
                        </div>
                    )}
                </div>
            )}
            {qState === 'ready' && questions.length === 0 && (
                <div className="shrink-0 border-b px-3 py-2 text-xs text-muted-foreground">
                    В методике нет вопросов — внешняя (авторская) методика: контент по лицензии не включён. Результат вносится вручную — кнопка «Создать протокол».
                </div>
            )}
            {qState === 'ready' && questions.length > 0 && (
                <div className="shrink-0 border-b">
                    <button type="button" className="flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/40"
                        onClick={() => setShowQuestions((v) => !v)}>
                        {showQuestions ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        <ListChecks className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>Вопросы методики ({questions.length})</span>
                    </button>
                    {showQuestions && (
                        <ol className="max-h-56 list-inside list-decimal space-y-1 overflow-auto px-4 pb-3 text-xs">
                            {questions.map((q, i) => (
                                <li key={q.id || i} className="text-muted-foreground">
                                    <span className="text-foreground">{q.text}</span>
                                    {q.required && <span className="text-destructive"> *</span>}
                                    {q.options && q.options.length > 0 && <span className="ml-1">— {q.options.join(' · ')}</span>}
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            )}

            <div className="min-h-0 flex-1 overflow-auto p-3 space-y-2">
                {loadError && (
                    <div className="rounded-lg border border-destructive/40 px-3 py-2 text-xs space-y-1.5">
                        <div className="text-sm font-medium text-destructive">Не удалось загрузить протоколы</div>
                        <div className="text-muted-foreground break-words">{loadError}</div>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => void load()}>
                            Повторить
                        </Button>
                    </div>
                )}
                <PollProtocolCards
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    emptyText={catalogItem?.manualOnly
                        ? 'Протоколов пока нет — у внешней методики результат вносится вручную («Создать протокол»).'
                        : 'Протоколов пока нет — нажмите «Пройти», чтобы заполнить методику.'}
                />
            </div>

            {filling && (
                <FillModal poll={filling} values={fillValues} setValues={setFillValues} busy={false}
                    subjectName={fillSubject?.name}
                    onCancel={() => { setFilling(null); setFillSubject(null); }} onSubmit={() => void submitFill()} />
            )}
            {manualOpen && resolvedType?.templateId && (
                <PollManualProtocolDialog
                    pollId={resolvedType.templateId}
                    pollName={pollName || code}
                    psyMode={psyMode}
                    onClose={() => setManualOpen(false)}
                    onSaved={() => void load()}
                />
            )}
            {clientPick && (
                <PollClientPickDialog
                    onSelect={(id, name) => { setClientPick(false); openFill({ id, name }); }}
                    onClose={() => setClientPick(false)}
                />
            )}
        </div>
    );
}
